/**
 * Charging-start decision telemetry. Every path that could send
 * POST /sessions/:uid/start records what it did, so a journey that never
 * started can be told apart from one whose request failed:
 *
 *   charging.start.skipped       { reason, metadata: { surface } }
 *   charging.start.request_sent  { metadata: { surface } }
 *   charging.start.accepted      { metadata: { surface, idempotent } }
 *   charging.start.rejected      { reason: <API error code> }   HTTP 4xx
 *   charging.start.failed        { reason: <error code> }       network / 5xx
 *
 * Correlation is the journey / client-session envelope `track()` adds, and
 * the /start request itself carries the journey headers the API joins on.
 * Nothing here reads a session id, station id, idTag, plate or amount.
 */
import { track } from './journey';

const SURFACES = new Set(['connect', 'monitor', 'retry']);
const CODE = /^[A-Za-z0-9_]{1,64}$/;
const code = (v) => (typeof v === 'string' && CODE.test(v) ? v : 'UNKNOWN');
const surfaceOf = (s) => (SURFACES.has(s) ? s : 'connect');

export function trackStartSkipped(surface, reason) {
  track('charging.start.skipped', { result: 'skipped', reason: code(reason), metadata: { surface: surfaceOf(surface) } });
}

export function trackStartRequestSent(surface) {
  track('charging.start.request_sent', { metadata: { surface: surfaceOf(surface) } });
}

/** `result` is what sessionsApi.startSession resolved with. */
export function trackStartResult(surface, result) {
  const metadata = { surface: surfaceOf(surface) };
  if (result?.ok) {
    track('charging.start.accepted', { result: 'accepted', metadata: { ...metadata, idempotent: result.idempotent === true } });
    return;
  }
  const status = Number(result?.httpStatus);
  if (status >= 400 && status < 500) {
    track('charging.start.rejected', { result: 'rejected', reason: code(result?.error), metadata });
  } else {
    track('charging.start.failed', { result: 'failed', reason: code(result?.error), metadata });
  }
}
